import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clipboard,
  Copy,
  ExternalLink,
  FileText,
  Globe2,
  Home,
  Info,
  KeyRound,
  Languages,
  RefreshCw,
  Search,
  ShieldCheck,
  XCircle
} from "lucide-react";
import { sampleAudit } from "./sampleAudit";
import type { AdsEntry, AdTrustAudit, Issue, Level } from "./types";
import "./styles.css";

type Language = "en" | "ko";
type OutputKey = "adsTxt" | "fixRequest" | "robotsAllow" | "report";

const labels = {
  en: {
    title: "AdTrust Inspector",
    subtitle: "ads.txt, robots.txt, and seller transparency checks for AdSense publishers.",
    domain: "Domain",
    publisher: "AdSense publisher ID",
    appAds: "Check app-ads.txt",
    inspect: "Inspect",
    sample: "Sample",
    overview: "Overview",
    ads: "ads.txt Analysis",
    crawl: "Crawler & Robots",
    issues: "Issues",
    seller: "Seller ID",
    generated: "Copy-ready fix",
    score: "Overall status",
    noPrivate: "No login. Only public files are read.",
    timing: "AdSense status can lag after a correct upload.",
    copied: "Copied",
    copy: "Copy",
    error: "The inspection could not run."
  },
  ko: {
    title: "AdTrust Inspector",
    subtitle: "AdSense 퍼블리셔를 위한 ads.txt, robots.txt, sellers.json 투명성 점검.",
    domain: "도메인",
    publisher: "AdSense publisher ID",
    appAds: "app-ads.txt 확인",
    inspect: "검사",
    sample: "샘플",
    overview: "개요",
    ads: "ads.txt 분석",
    crawl: "크롤러 & robots",
    issues: "이슈",
    seller: "Seller ID",
    generated: "복사용 수정안",
    score: "전체 상태",
    noPrivate: "로그인 없이 공개 파일만 읽습니다.",
    timing: "정상 업로드 후에도 AdSense 반영은 지연될 수 있습니다.",
    copied: "복사됨",
    copy: "복사",
    error: "검사를 실행하지 못했습니다."
  }
};

const outputTabs: Array<{ key: OutputKey; label: string }> = [
  { key: "adsTxt", label: "ads.txt" },
  { key: "fixRequest", label: "Fix request" },
  { key: "robotsAllow", label: "robots.txt" },
  { key: "report", label: "Report" }
];

function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [domain, setDomain] = useState("example.com");
  const [publisher, setPublisher] = useState("pub-1234567890123456");
  const [includeAppAds, setIncludeAppAds] = useState(false);
  const [audit, setAudit] = useState<AdTrustAudit>(sampleAudit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeOutput, setActiveOutput] = useState<OutputKey>("fixRequest");
  const t = labels[language];
  const topEntries = useMemo(() => audit.ads.entries.slice(0, 8), [audit]);
  const output = audit.generated[activeOutput];

  async function runInspect(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        domain,
        publisher,
        app: includeAppAds ? "1" : "0"
      });
      const response = await fetch(`/api/inspect?${params.toString()}`);
      const payload = (await response.json()) as AdTrustAudit | { error?: string };
      if (!response.ok) {
        throw new Error("error" in payload ? payload.error ?? t.error : t.error);
      }
      setAudit(payload as AdTrustAudit);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t.error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="shell">
      <aside className="rail">
        <a data-google-vignette="false" className="brand" href="/" aria-label="AdTrust Inspector home">
          <span>
            <ShieldCheck size={24} />
          </span>
          <strong>{t.title}</strong>
        </a>

        <nav aria-label="Sections">
          <a data-google-vignette="false" href="#overview" className="active">
            <Home size={17} />
            {t.overview}
          </a>
          <a data-google-vignette="false" href="#ads">
            <FileText size={17} />
            {t.ads}
          </a>
          <a data-google-vignette="false" href="#crawl">
            <Bot size={17} />
            {t.crawl}
          </a>
          <a data-google-vignette="false" href="#issues">
            <AlertTriangle size={17} />
            {t.issues}
          </a>
        </nav>

        <form className="inspect-form" onSubmit={runInspect}>
          <label>
            <span>{t.domain}</span>
            <input value={domain} onChange={(event) => setDomain(event.target.value)} spellCheck={false} />
          </label>
          <label>
            <span>{t.publisher}</span>
            <input value={publisher} onChange={(event) => setPublisher(event.target.value)} spellCheck={false} />
          </label>
          <label className="check-row">
            <input type="checkbox" checked={includeAppAds} onChange={(event) => setIncludeAppAds(event.target.checked)} />
            <span>{t.appAds}</span>
          </label>
          <div className="form-actions">
            <button type="button" className="ghost-button" onClick={() => setAudit(sampleAudit)}>
              {t.sample}
            </button>
            <button type="submit" className="primary-button" disabled={loading}>
              {loading ? <RefreshCw size={17} className="spin" /> : <Search size={17} />}
              {t.inspect}
            </button>
          </div>
        </form>

        <button className="language-toggle" type="button" onClick={() => setLanguage(language === "en" ? "ko" : "en")}>
          <Languages size={17} />
          {language.toUpperCase()}
        </button>

        <div className="rail-note">
          <Info size={17} />
          <span>{t.noPrivate}</span>
          <small>{t.timing}</small>
        </div>
      </aside>

      <main className="workspace">
        <header className="hero" id="overview">
          <div>
            <p className="eyebrow">Publisher trust preflight</p>
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
          </div>
          <img src="/adtrust-dashboard.png" alt="AdTrust Inspector dashboard preview" />
        </header>

        {error ? (
          <div className="error" role="alert">
            <AlertTriangle size={18} />
            {error}
          </div>
        ) : null}

        <section className="metric-grid">
          <Metric label={t.score} value={`${audit.score}/100`} detail={audit.scoreLabel} level={scoreLevel(audit.score)} icon={<ShieldCheck size={22} />} />
          <Metric label="Issues found" value={String(audit.summary.errors + audit.summary.warnings + audit.summary.notices)} detail={`${audit.summary.errors} errors, ${audit.summary.warnings} warnings`} level={audit.summary.errors ? "fail" : audit.summary.warnings ? "warn" : "pass"} icon={<AlertTriangle size={22} />} />
          <Metric label="ads.txt lines" value={String(audit.ads.entries.length)} detail={`${audit.summary.authorizedSellers} seller records`} level={audit.ads.entries.length ? "pass" : "fail"} icon={<FileText size={22} />} />
          <Metric label="Publisher ID" value={audit.publisher.found ? "Found" : audit.publisher.publisherId ? "Missing" : "Optional"} detail={audit.publisher.publisherId || "No ID entered"} level={audit.publisher.found ? "pass" : audit.publisher.publisherId ? "fail" : "info"} icon={<KeyRound size={22} />} />
        </section>

        <section className="content-grid">
          <div className="wide-stack">
            <Panel title={t.ads} icon={<FileText size={18} />} id="ads">
              <AdsTable entries={topEntries} />
              <div className="directive-strip">
                <span>{audit.ads.directives.length} directives</span>
                <span>{audit.ads.comments} comments</span>
                <span>{audit.ads.duplicateKeys.length} duplicate groups</span>
              </div>
            </Panel>

            <Panel title={t.crawl} icon={<Bot size={18} />} id="crawl">
              <div className="crawl-grid">
                <FetchTile label="HTTPS" check={audit.fetches.httpsAds} />
                <FetchTile label="HTTP" check={audit.fetches.httpAds} />
                <FetchTile label="robots.txt" check={audit.fetches.robots} level={audit.robots.status} />
                <FetchTile label="app-ads.txt" check={audit.fetches.appAds} level={audit.fetches.appAds.ok ? "pass" : "info"} />
              </div>
            </Panel>

            <Panel title={t.issues} icon={<AlertTriangle size={18} />} id="issues">
              <IssueList issues={audit.issues} />
            </Panel>
          </div>

          <div className="side-stack">
            <Panel title={t.seller} icon={<KeyRound size={18} />}>
              <div className="seller-box">
                <LevelIcon level={audit.publisher.sellersJson.status} />
                <strong>{audit.publisher.publisherId || "Publisher ID not entered"}</strong>
                <p>{audit.publisher.sellersJson.detail}</p>
                <a data-google-vignette="false" href={audit.publisher.sellersJson.source} target="_blank" rel="noreferrer">
                  Google sellers.json <ExternalLink size={14} />
                </a>
              </div>
            </Panel>

            <Panel title={t.generated} icon={<Clipboard size={18} />}>
              <div className="output-tabs" role="tablist" aria-label="Generated outputs">
                {outputTabs.map((tab) => (
                  <button type="button" key={tab.key} className={activeOutput === tab.key ? "active" : ""} onClick={() => setActiveOutput(tab.key)}>
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="output-box">
                <pre>{output}</pre>
                <CopyButton value={output} copiedLabel={t.copied} copyLabel={t.copy} />
              </div>
            </Panel>
          </div>
        </section>
      </main>
      <footer className="site-footer">
        <span>Independent AdSense readiness checker. Public files only; no credentials are collected.</span>
        <nav aria-label="Site policies">
          <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          <a data-google-vignette="false" href="/about.html">About</a>
        </nav>
      </footer>
    </div>
  );
}

function Panel({ title, icon, children, id }: { title: string; icon: React.ReactNode; children: React.ReactNode; id?: string }) {
  return (
    <section className="panel" id={id}>
      <div className="panel-title">
        <span>{icon}</span>
        <h2>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Metric({ label, value, detail, level, icon }: { label: string; value: string; detail: string; level: Level; icon: React.ReactNode }) {
  return (
    <article className="metric">
      <span className={`metric-icon ${level}`}>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{detail}</p>
      </div>
    </article>
  );
}

function AdsTable({ entries }: { entries: AdsEntry[] }) {
  if (!entries.length) {
    return <div className="empty-state">No ads.txt seller records were parsed.</div>;
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Line</th>
            <th>Exchange</th>
            <th>Account</th>
            <th>Type</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={`${entry.line}-${entry.raw}`}>
              <td>{entry.line}</td>
              <td>{entry.exchange}</td>
              <td>
                <code>{entry.accountId}</code>
              </td>
              <td>
                <span className="type-chip">{entry.relationship}</span>
              </td>
              <td>
                <LevelBadge level={entry.level} label={entry.issue} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FetchTile({ label, check, level }: { label: string; check: { status: number | null; ok: boolean; finalUrl: string; elapsedMs: number; error: string }; level?: Level }) {
  const computed: Level = level ?? (check.ok ? "pass" : "fail");
  return (
    <article className="fetch-tile">
      <div>
        <LevelIcon level={computed} />
        <strong>{label}</strong>
      </div>
      <p>{check.status ? `${check.status} ${check.ok ? "OK" : check.error}` : check.error}</p>
      <small>{check.finalUrl}</small>
      <em>{check.elapsedMs ? `${check.elapsedMs} ms` : "not requested"}</em>
    </article>
  );
}

function IssueList({ issues }: { issues: Issue[] }) {
  const visible = issues.slice(0, 12);
  const remaining = Math.max(0, issues.length - visible.length);

  return (
    <ul className="issue-list">
      {visible.map((item, index) => (
        <li key={`${item.title}-${index}`}>
          <LevelIcon level={item.level} />
          <div>
            <strong>{item.title}</strong>
            <span>{item.detail}</span>
          </div>
          <code>{item.location}</code>
        </li>
      ))}
      {remaining ? (
        <li className="issue-more">
          <Info size={18} />
          <div>
            <strong>{remaining} more notices</strong>
            <span>Open the generated report for the full inspection detail.</span>
          </div>
          <code>Report</code>
        </li>
      ) : null}
    </ul>
  );
}

function CopyButton({ value, copiedLabel, copyLabel }: { value: string; copiedLabel: string; copyLabel: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button className="copy-button" type="button" onClick={copy}>
      <Copy size={15} />
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}

function LevelBadge({ level, label }: { level: Level; label: string }) {
  return <span className={`badge ${level}`}>{label}</span>;
}

function LevelIcon({ level }: { level: Level }) {
  if (level === "pass") return <CheckCircle2 className="level-icon pass" size={18} />;
  if (level === "fail") return <XCircle className="level-icon fail" size={18} />;
  if (level === "warn") return <AlertTriangle className="level-icon warn" size={18} />;
  return <Info className="level-icon info" size={18} />;
}

function scoreLevel(score: number): Level {
  if (score >= 82) return "pass";
  if (score >= 60) return "warn";
  return "fail";
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
